/**
 * Lobby surface: roster, invite panel, character slot switcher, ready toggle, emotes and the host's launch
 * panel (docs/ARCHITECTURE.md §3, §12). `props.children` renders inside the voice panel placeholder area so
 * the voice task can slot its panel in without touching this file — see the `<Lobby>` call in
 * `SessionPage.tsx` if you need to wire that up.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  CHARACTER_SLOTS,
  CHARACTER_SLOT_IDS,
  EMOTE_IDS,
  type ActionOutcome,
  type CharacterSlotId,
  type EmoteId,
  type ModuleManifest,
  type ParticipantPublic,
  type TeamAssignment,
} from '@momentum/shared';
import { ApiError, addDemoPlayers, getInviteDetails, getModules, removeDemoPlayers } from '../api.ts';
import { useSessionContext } from '../session/SessionContext.ts';
import { HOST_SESSION_KEY, clearCredentials } from '../session/credentials.ts';
import { CharacterBadge } from '../components/CharacterBadge.tsx';
import { BerryAvatar } from '../shell/BerryAvatar.tsx';
import { strings } from '../shell/strings.ts';
import { EMOTE_ANIMATIONS, useLobbyEmotes } from './emotes.ts';
import { MOMENTUM_BOARD_V2_ID } from '@momentum/modules/manifests';
import './meeting-lobby.css';
import { useHostNarration } from '../narration/HostNarration.tsx';
import type { UseVoiceResult } from '../voice/useVoice.ts';
import { LobbyScene } from './LobbyScene.tsx';
import { Link } from '../shell/router.tsx';
import { ResetForNewGroup } from '../session/ResetForNewGroup.tsx';

const MEETING_DEMO_SELECTION = `${MOMENTUM_BOARD_V2_ID}:meeting-demo`;

function isActiveParticipant(participant: ParticipantPublic): boolean {
  return participant.connection !== 'left' && participant.connection !== 'removed';
}

export interface LobbyProps {
  children?: ReactNode;
  voice?: UseVoiceResult;
}

export function Lobby({ children, voice }: LobbyProps) {
  const { client, view, self, reducedMotion, sessionId, credentials } = useSessionContext();
  const session = view.session;
  const narration = useHostNarration();
  const [arrival, setArrival] = useState('');
  const [welcomeCaption, setWelcomeCaption] = useState('Welcome to Momentum.');
  useEffect(() => {
    if (!narration.playing) return;
    setWelcomeCaption('Welcome to Momentum.');
    const middle = setTimeout(() => setWelcomeCaption('Say hello in the huddle, try a move, and get comfortable.'), 1500);
    const final = setTimeout(() => setWelcomeCaption('Your host will start when everyone is ready.'), 4900);
    return () => { clearTimeout(middle); clearTimeout(final); };
  }, [narration.playing]);
  const previousConnections = useRef<Map<string, string> | null>(null);
  useEffect(() => {
    const connected = session.participants.filter(p => p.role === 'player' && p.connection === 'connected');
    const previous = previousConnections.current;
    previousConnections.current = new Map(session.participants.map(p => [p.id, p.connection]));
    if (!previous) return;
    const arrivals = connected.filter(p => previous.get(p.id) !== 'connected' && p.id !== self?.participantId);
    if (arrivals.length) setArrival(arrivals.length === 1 ? `${arrivals[0]!.displayName} is here. Welcome!` : `${arrivals.length} teammates have joined. Welcome!`);
  }, [session.participants, self?.participantId]);
  useEffect(() => {
    if (!arrival) return;
    const timer = setTimeout(() => setArrival(''), 5000);
    return () => clearTimeout(timer);
  }, [arrival]);
  const activeEmotes = useLobbyEmotes(client);
  const activeParticipants = session.participants.filter(isActiveParticipant);
  const currentParticipant = self ? activeParticipants.find((participant) => participant.id === self.participantId) : null;

  useEffect(() => {
    if (!self?.isHost) return;
    try { localStorage.setItem(HOST_SESSION_KEY, sessionId); } catch { /* Browser storage may be unavailable. */ }
  }, [self?.isHost, sessionId]);

  async function leave() {
    const outcome = await client.send('session.leave', {}, 'session');
    if (outcome.ok) clearCredentials(sessionId, credentials.token);
  }

  const players = activeParticipants.filter(participant => participant.role === 'player');
  const readyCount = players.filter(participant => participant.ready && participant.connection === 'connected').length;
  return (
    <div className="meeting-lobby">
      <header className="meeting-lobby__header">
        <div><span className="meeting-lobby__eyebrow">Momentum · The gathering</span>
          <h1>Good things start together.</h1>
          <p>{self?.isHost ? 'Bring your team in. The adventure starts when you are ready.' : 'You’re in. Settle in with your team before the adventure begins.'}</p>
        </div>
        <span className="meeting-lobby__count" role="status">{players.length}/{session.settings.maxPlayers} here · {readyCount} ready</span>
      </header>

      <section className="meeting-lobby__gathering" aria-label="Your team gathering on the island">
        <div className="meeting-lobby__scene"><LobbyScene participants={players} selfId={self?.participantId} reducedMotion={reducedMotion} emotes={activeEmotes} speakingParticipantIds={voice?.provider === 'livekit' && voice.status === 'connected' ? [...voice.speaking] : []} hostSpeaking={narration.playing} hostMessage={narration.playing ? welcomeCaption : arrival} /></div>
        <section className="meeting-lobby__participants" aria-label={strings.lobby.participants}>
          <ul className="meeting-lobby__participant-list">
            {activeParticipants.map(participant => <ParticipantRow key={participant.id} participant={participant}
              isYou={self?.participantId === participant.id} emoteId={activeEmotes[participant.id]?.emoteId ?? null}
              reducedMotion={reducedMotion} canRemove={false}
              onRemove={() => client.send('session.remove_participant', { participantId: participant.id }, 'session')} />)}
          </ul>
        </section>
        {self?.role === 'player' ? <ReadyAndEmotes ready={currentParticipant?.ready ?? false} onToggleReady={ready => client.send('session.set_ready', { ready }, 'session')}
          onEmote={emoteId => client.send('session.emote', { emoteId }, 'session')} hideReady /> : null}
        <p className="meeting-lobby__scene-caption">{players.length < 2 ? 'A space for your team. Who’s joining you?' : 'The team is coming together.'}</p>
      </section>

      <aside className="meeting-lobby__side">
        {self?.isHost ? <InvitePanel sessionId={sessionId} token={credentials.token} embedded /> : <div className="meeting-lobby__welcome"><span className="meeting-lobby__eyebrow">You’re on the guest list</span><h2>Make yourself at home.</h2><p>Say hello in the huddle and mark yourself ready. Your host will start the game.</p></div>}
        <div className={`meeting-lobby__start${self?.isHost ? ' meeting-lobby__start--host' : ''}`}>
          {self?.role === 'player' && !self.isHost ? <button type="button" className={`button meeting-lobby__ready${currentParticipant?.ready ? ' is-ready' : ''}`}
            onClick={() => client.send('session.set_ready', { ready: !currentParticipant?.ready }, 'session')}>
            {currentParticipant?.ready ? '✓ You’re ready' : 'I’m ready'}
          </button> : null}
          {self?.isHost ? <><MeetingLaunch /><p className="meeting-lobby__hosting">Hosting as <strong>{currentParticipant?.displayName ?? 'Host'}</strong></p></> : <p className="meeting-lobby__waiting" role="status">Waiting for the host to start.</p>}
        </div>
      </aside>

      <section className="meeting-lobby__huddle" aria-label="Huddle">
        <div className="meeting-lobby__section-heading"><h2>Already together</h2><span>Your huddle stays with you throughout the game</span></div>
        {children ?? <p className="text-muted">{strings.voice.simulatedBadge}</p>}
      </section>

      <details className="meeting-lobby__settings">
        <summary>Room & character settings</summary>
        {self?.role === 'player' ? <><CharacterSlotSwitcher currentSlot={currentParticipant?.characterSlot ?? null} availableSlots={session.availableSlots}
          onSelect={slot => client.send('session.set_character', { characterSlot: slot }, 'session')} /></> : null}
        {self?.isHost ? <><ResetForNewGroup /><HostAccess sessionId={sessionId} credentials={credentials} />
          {activeParticipants.filter(p => !p.simulated && p.id !== self.participantId).map(p => <button key={p.id} type="button" className="button button-sm" onClick={() => void client.send('session.remove_participant', { participantId: p.id }, 'session')}>Remove {p.displayName}</button>)}
          {import.meta.env.DEV ? <details className="meeting-lobby__developer"><summary>Developer options</summary><LaunchPanel /></details> : null}
        </> : null}
        {self ? <button type="button" className="button button-sm" onClick={() => void leave()}>{strings.lobby.leave}</button> : null}
      </details>
    </div>
  );
}

function ParticipantRow({
  participant,
  isYou,
  emoteId,
  reducedMotion,
  canRemove,
  onRemove,
}: {
  participant: ParticipantPublic;
  isYou: boolean;
  emoteId: EmoteId | null;
  reducedMotion: boolean;
  canRemove: boolean;
  onRemove: () => Promise<ActionOutcome>;
}) {
  const animation = emoteId ? EMOTE_ANIMATIONS[emoteId] : null;
  return (
    <li className="meeting-lobby__participant">
      <span className={`meeting-lobby__avatar${!reducedMotion && animation ? ` ${animation.className}` : ''}`}>
        <BerryAvatar slot={participant.characterSlot} size={42} />
      </span>
      <span className="meeting-lobby__identity">
        <strong>{participant.displayName}</strong>
        <small>{[isYou ? strings.lobby.you : null, participant.isHost ? strings.lobby.host : null, participant.simulated ? 'Demo teammate' : null].filter(Boolean).join(' · ')}</small>
      </span>
      <span className="meeting-lobby__status" aria-label="Status">
        {participant.role === 'player' ? (
          <span className={participant.ready ? 'is-ready' : undefined}>
            {participant.ready ? strings.lobby.ready : strings.lobby.notReady}
          </span>
        ) : null}
        {participant.connection !== 'connected' ? (
          <span className="is-disconnected">{strings.lobby.disconnected}</span>
        ) : null}
        {reducedMotion && animation ? <span>{animation.verb}</span> : null}
        {canRemove ? <button type="button" className="button button-sm meeting-lobby__remove" onClick={() => void onRemove()}>Remove</button> : null}
      </span>
    </li>
  );
}

function MeetingLaunch() {
  const { client, view, self, sessionId, credentials } = useSessionContext();
  const [launching, setLaunching] = useState(false);
  const [rejectMessage, setRejectMessage] = useState<string | null>(null);
  const [demoFlow, setDemoFlow] = useState<'idle' | 'adding' | 'waiting-roster' | 'waiting-ready'>('idle');
  const [removingDemo, setRemovingDemo] = useState(false);
  const players = view.session.participants.filter((participant) => participant.role === 'player' && isActiveParticipant(participant));
  const rosterInRange = players.length >= 2 && players.length <= 6;
  const allReady = players.every((participant) => participant.ready && participant.connection === 'connected');
  const canLaunch = rosterInRange && allReady;
  const currentPlayer = players.find((participant) => participant.id === self?.participantId);
  const hint = players.length === 4
    ? 'Momentum will alternate Decision Maker and Hot Potato after each board round.'
    : 'Momentum will bring the group into Hot Potato after each board round.';

  async function launch() {
    setLaunching(true);
    setRejectMessage(null);
    const outcome = await client.send('session.launch', {
      moduleId: MOMENTUM_BOARD_V2_ID,
      participantIds: players.map((participant) => participant.id),
      teams: null,
      config: { meetingDemo: true },
    }, 'session');
    if (!outcome.ok) setRejectMessage(outcome.message);
    setLaunching(false);
  }

  useEffect(() => {
    if (demoFlow === 'waiting-roster' && players.length >= Math.min(4, view.session.settings.maxPlayers)) {
      if (currentPlayer?.ready) setDemoFlow('waiting-ready');
      else {
        setDemoFlow('waiting-ready');
        void client.send('session.set_ready', { ready: true }, 'session').then((outcome) => {
          if (!outcome.ok) { setRejectMessage(outcome.message); setDemoFlow('idle'); }
        });
      }
    } else if (demoFlow === 'waiting-ready' && canLaunch) {
      setDemoFlow('idle');
      void launch();
    }
  }, [demoFlow, players.length, currentPlayer?.ready, canLaunch]);

  async function startWithDemoTeammates() {
    setRejectMessage(null);
    setDemoFlow('adding');
    try {
      await addDemoPlayers(sessionId, credentials.token);
      setDemoFlow('waiting-roster');
    } catch (error) {
      setRejectMessage(error instanceof ApiError ? error.message : strings.common.error);
      setDemoFlow('idle');
    }
  }

  async function normalStart() {
    setRejectMessage(null);
    if (!currentPlayer?.ready) {
      const outcome = await client.send('session.set_ready', { ready: true }, 'session');
      if (!outcome.ok) { setRejectMessage(outcome.message); return; }
      setDemoFlow('waiting-ready');
      return;
    }
    await launch();
  }

  async function removeDemo() {
    setRemovingDemo(true);
    try { await removeDemoPlayers(sessionId, credentials.token); }
    catch (error) { setRejectMessage(error instanceof ApiError ? error.message : strings.common.error); }
    setRemovingDemo(false);
  }

  return (
    <section className="meeting-lobby__launch">
      <button type="button" className="button button-primary meeting-lobby__normal-start" disabled={!rosterInRange || players.some((player) => player.id !== self?.participantId && (!player.ready || player.connection !== 'connected')) || launching || demoFlow !== 'idle'} onClick={() => void normalStart()}>
        Start game
      </button>
      <button type="button" className="button meeting-lobby__demo-start" disabled={demoFlow !== 'idle' || launching} onClick={() => void startWithDemoTeammates()}>
        {demoFlow === 'waiting-ready' && !canLaunch ? 'Waiting for teammates…' : demoFlow !== 'idle' || launching ? 'Starting…' : 'Play with NPCs'}
      </button>
      {demoFlow === 'waiting-roster' || demoFlow === 'waiting-ready' ? <button type="button" className="button button-sm meeting-lobby__cancel-demo" onClick={() => setDemoFlow('idle')}>Cancel start</button> : null}
      {players.some((participant) => participant.simulated) ? <button type="button" className="button button-sm meeting-lobby__remove-demo" disabled={removingDemo || demoFlow !== 'idle'} onClick={() => void removeDemo()}>{removingDemo ? 'Removing…' : 'Remove demo teammates'}</button> : null}
      <p>{!rosterInRange ? 'Invite people with the link above, or play now with NPCs.' : players.some(player => player.id !== self?.participantId && (!player.ready || player.connection !== 'connected')) ? 'Other people still need to be connected and ready.' : hint}</p>
      {rejectMessage ? <p className="badge badge-danger" role="alert">{rejectMessage}</p> : null}
    </section>
  );
}

function encodeHostAccess(sessionId: string, credentials: import('../session/credentials.ts').StoredCredentials): string {
  const bytes = new TextEncoder().encode(JSON.stringify({ sessionId, participant: credentials }));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function HostAccess({ sessionId, credentials }: { sessionId: string; credentials: import('../session/credentials.ts').StoredCredentials }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/host#access=${encodeHostAccess(sessionId, credentials)}`;
  async function copy() {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2_000);
  }
  return (
    <details className="meeting-lobby__host-access">
      <summary>Host access</summary>
      <p>This private link controls the session. Keep it separate from the guest QR.</p>
      <label htmlFor="host-access-link">Private host link</label>
      <input id="host-access-link" type="text" readOnly value={link} onFocus={(event) => event.currentTarget.select()} />
      <div className="meeting-lobby__host-actions">
        <button type="button" className="button button-sm" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy private host link'}</button>
        <Link className="button button-sm" to={`/s/${encodeURIComponent(sessionId)}`}>Open host controls</Link>
      </div>
    </details>
  );
}

export function InvitePanel({
  sessionId,
  token,
  compact = false,
  defaultOpen = false,
  embedded = false,
}: {
  sessionId: string;
  token: string;
  compact?: boolean;
  defaultOpen?: boolean;
  embedded?: boolean;
}) {
  const [details, setDetails] = useState<Awaited<ReturnType<typeof getInviteDetails>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);
  const { client, view } = useSessionContext();

  useEffect(() => {
    let cancelled = false;
    setDetails(null);
    setCopied(false);
    setError(null);
    getInviteDetails(sessionId, token)
      .then((value) => {
        if (!cancelled) setDetails(value);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : strings.common.error);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, token, view.session.shortCode]);

  async function rotate() {
    setRotating(true);
    const outcome = await client.send('session.rotate_invite', {}, 'session');
    if (outcome.ok) {
      try {
        setDetails(await getInviteDetails(sessionId, token));
        setError(null);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : strings.common.error);
      }
    } else {
      setError(outcome.message);
    }
    setRotating(false);
  }

  async function copyInvite() {
    if (!details) return;
    try {
      await navigator.clipboard.writeText(details.inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      setError(strings.lobby.inviteCopyFailed);
    }
  }

  async function shareInvite() {
    if (!details) return;
    if (typeof navigator.share !== 'function') {
      await copyInvite();
      return;
    }
    try {
      await navigator.share({ title: strings.lobby.inviteShareTitle, url: details.inviteUrl });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(strings.lobby.inviteCopyFailed);
    }
  }

  const content = (
    <section className="stack-sm" aria-label={strings.lobby.inviteTitle}>
      <h2>{embedded ? 'Invite your team' : strings.lobby.inviteTitle}</h2>{embedded ? <p className="meeting-invite__intro">Scan the code or share the link. No account needed.</p> : null}
      {error ? <p className="badge badge-danger">{error}</p> : null}
      {details ? (
        <div className="meeting-invite">
          {details.reachability === 'localhost' ? (
            <p className="badge badge-danger">{strings.lobby.inviteLocalhostCaveat}</p>
          ) : null}
          <div className="meeting-invite__qr"
            aria-hidden="true"
            // Server-generated SVG (docs/ARCHITECTURE.md §8); encodes exactly `inviteUrl`.
            dangerouslySetInnerHTML={{ __html: details.qrSvg }}
          />
          <div className="meeting-invite__actions">
            <button type="button" className="button button-primary" onClick={() => void copyInvite()}>
              {copied ? strings.lobby.inviteCopied : strings.lobby.inviteCopy}
            </button>{' '}
            {typeof navigator.share === 'function' ? (
              <button type="button" className="button" onClick={() => void shareInvite()}>{strings.lobby.inviteShare}</button>
            ) : null}
          </div>
          <details className="meeting-invite__more">
            <summary>Code and link options</summary>
            <div className="field">
              <label htmlFor="invite-url">{strings.lobby.inviteLink}</label>
              <input id="invite-url" type="text" readOnly value={details.inviteUrl} onFocus={(e) => e.currentTarget.select()} />
            </div>
            <p>{strings.lobby.inviteCode}: <strong>{details.shortCode}</strong></p>
            <button type="button" className="button button-sm" onClick={rotate} disabled={rotating}>
              {rotating ? strings.lobby.inviteRotating : strings.lobby.inviteRotate}
            </button>
          </details>
        </div>
      ) : (
        <p className="text-muted">{strings.session.loading}</p>
      )}
    </section>
  );

  if (embedded) return <div className="meeting-lobby__invitation">{content}</div>;

  if (compact) {
    return (
      <details className="card host-invite-control" open={defaultOpen || undefined}>
        <summary>{strings.lobby.invitePersistent}</summary>
        {content}
      </details>
    );
  }

  return <div className="card">{content}</div>;
}

function CharacterSlotSwitcher({
  currentSlot,
  availableSlots,
  onSelect,
}: {
  currentSlot: CharacterSlotId | null;
  availableSlots: CharacterSlotId[];
  onSelect: (slot: CharacterSlotId) => void;
}) {
  return (
    <section className="card stack-sm" aria-label={strings.lobby.characterLabel}>
      <h2>{strings.lobby.characterLabel}</h2>
      <div className="row">
        {CHARACTER_SLOT_IDS.map((slotId) => {
          const appearance = CHARACTER_SLOTS[slotId];
          const isMine = currentSlot === slotId;
          const isFree = isMine || availableSlots.includes(slotId);
          return (
            <button
              key={slotId}
              type="button"
              className={isMine ? 'button card-selected' : 'button'}
              disabled={!isFree}
              onClick={() => onSelect(slotId)}
              aria-pressed={isMine}
            >
              <CharacterBadge slot={slotId} displayName={appearance.label} size={32} />
              <span className="text-muted">{isFree ? strings.lobby.characterSlotFree : strings.lobby.characterSlotTaken}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ReadyAndEmotes({
  ready,
  onToggleReady,
  onEmote,
  hideReady = false,
}: {
  ready: boolean;
  onToggleReady: (ready: boolean) => void;
  onEmote: (emoteId: EmoteId) => Promise<ActionOutcome>;
  hideReady?: boolean;
}) {
  const [rateLimited, setRateLimited] = useState(false);

  async function emote(id: EmoteId) {
    const outcome = await onEmote(id);
    if (!outcome.ok && outcome.code === 'rate_limited') {
      setRateLimited(true);
      setTimeout(() => setRateLimited(false), 3000);
    }
  }

  return (
    <section className={hideReady ? "meeting-lobby__moves" : "card stack-sm"} aria-label="Character moves">
      {!hideReady ? (
        <div className="row-between">
          <h2>{strings.lobby.title}</h2>
          <button type="button" className="button button-primary" onClick={() => onToggleReady(!ready)}>
            {ready ? strings.lobby.readyToggleOn : strings.lobby.readyToggleOff}
          </button>
        </div>
      ) : null}
      <h3>Try a move</h3>
      <div className="row">
        {EMOTE_IDS.map((id) => (
          <button key={id} type="button" className="button" disabled={rateLimited} onClick={() => emote(id)}>
            {{ wave: 'Wave', bounce: 'Dance', spin: 'Spin', cheer: 'Cheer' }[id]}
          </button>
        ))}
      </div>
      {rateLimited ? <p className="badge badge-danger">{strings.lobby.emoteSlowDown}</p> : null}
    </section>
  );
}

function LaunchPanel() {
  const { client, view } = useSessionContext();
  const session = view.session;
  const [manifests, setManifests] = useState<ModuleManifest[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<Set<string>>(new Set());
  const [teamAssignment, setTeamAssignment] = useState<Record<string, number>>({});
  const [launching, setLaunching] = useState(false);
  const [rejectMessage, setRejectMessage] = useState<string | null>(null);

  const players = useMemo(
    () => session.participants.filter((participant) => participant.role === 'player' && isActiveParticipant(participant)),
    [session.participants],
  );

  useEffect(() => {
    getModules()
      .then((res) => {
        const launchable = res.modules.filter((m) => m.launchableFromLobby);
        setManifests(launchable);
        setSelectedModuleId((prev) => prev ?? launchable[0]?.id ?? null);
      })
      .catch(() => setManifests([]));
  }, []);

  useEffect(() => {
    setSelectedParticipantIds(new Set(players.filter((p) => p.ready).map((p) => p.id)));
  }, [players]);

  const selectedManifest = manifests.find((m) => m.id === (selectedModuleId === MEETING_DEMO_SELECTION ? MOMENTUM_BOARD_V2_ID : selectedModuleId)) ?? null;
  const meetingDemoSelected = selectedModuleId === MEETING_DEMO_SELECTION;

  function toggleParticipant(id: string) {
    setSelectedParticipantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function launch() {
    if (!selectedManifest) return;
    setLaunching(true);
    setRejectMessage(null);
    const participantIds = [...selectedParticipantIds];
    let teams: TeamAssignment[] | null = null;
    if (selectedManifest.participation === 'teams' && selectedManifest.teams) {
      const count = selectedManifest.teams.count;
      teams = Array.from({ length: count }, (_, i) => {
        const teamNumber = i + 1;
        return {
          teamId: String(teamNumber),
          label: `Team ${teamNumber}`,
          participantIds: participantIds.filter((id) => (teamAssignment[id] ?? 1) === teamNumber),
        };
      });
    }
    const outcome = await client.send(
      'session.launch',
      { moduleId: selectedManifest.id, participantIds, teams, config: meetingDemoSelected ? { meetingDemo: true } : undefined },
      'session',
    );
    if (!outcome.ok) setRejectMessage(outcome.message);
    setLaunching(false);
  }

  if (manifests.length === 0) {
    return (
      <section className="card">
        <h2>{strings.lobby.launchTitle}</h2>
        <p className="text-muted">{strings.lobby.launchNoModules}</p>
      </section>
    );
  }

  return (
    <section className="card stack-sm">
      <h2>{strings.lobby.launchTitle}</h2>
      <div className="field">
        <label htmlFor="launch-module">{strings.lobby.launchModule}</label>
        <select id="launch-module" value={selectedModuleId ?? ''} onChange={(e) => setSelectedModuleId(e.target.value)}>
          {manifests.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} ({m.players.min}-{m.players.max} players)
            </option>
          ))}
          {manifests.some((m) => m.id === MOMENTUM_BOARD_V2_ID) ? (
            <option value={MEETING_DEMO_SELECTION}>{strings.lobby.meetingDemoLabel}</option>
          ) : null}
        </select>
      </div>
      {meetingDemoSelected ? <p className="text-muted">{strings.lobby.meetingDemoHint}</p> : null}

      <fieldset className="stack-sm" style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend>{strings.lobby.launchParticipants}</legend>
        {players.map((p) => (
          <label key={p.id} className="choice-row">
            <input type="checkbox" checked={selectedParticipantIds.has(p.id)} onChange={() => toggleParticipant(p.id)} />
            {p.displayName}
          </label>
        ))}
      </fieldset>

      {selectedManifest?.participation === 'teams' && selectedManifest.teams ? (
        <fieldset className="stack-sm" style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend>{strings.lobby.launchTeams}</legend>
          {[...selectedParticipantIds].map((id) => {
            const participant = players.find((p) => p.id === id);
            if (!participant) return null;
            return (
              <div key={id} className="choice-row">
                <span style={{ minWidth: 120 }}>{participant.displayName}</span>
                <select
                  aria-label={`Team for ${participant.displayName}`}
                  value={teamAssignment[id] ?? 1}
                  onChange={(e) => setTeamAssignment((prev) => ({ ...prev, [id]: Number(e.target.value) }))}
                >
                  {Array.from({ length: selectedManifest.teams!.count }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      Team {n}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </fieldset>
      ) : null}

      {rejectMessage ? (
        <p className="badge badge-danger" role="alert">
          {rejectMessage}
        </p>
      ) : null}

      <div>
        <button type="button" className="button button-primary" disabled={launching || !selectedManifest} onClick={launch}>
          {launching ? strings.lobby.launchLaunching : strings.lobby.launchButton}
        </button>
      </div>
    </section>
  );
}
